import { motion, useAnimationControls } from "framer-motion";
import { useEffect } from "react";

/**
 * AuthPanelBg — fond animé du panel gauche.
 * Inspiré Stripe/Linear : orbs flottants, grille de points subtile,
 * anneaux concentriques qui pulsent, wave SVG continue.
 */
export const AuthPanelBg = () => {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* ── Grille de points ── */}
      <svg
        className="absolute inset-0 h-full w-full opacity-[0.18]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="dots" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.2" fill="#7c5fcf" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dots)" />
      </svg>

      {/* ── Orb principal (haut-gauche) ── */}
      <motion.div
        className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(71,44,179,0.22) 0%, transparent 70%)",
        }}
        animate={{
          scale: [1, 1.12, 1],
          x: [0, 20, 0],
          y: [0, 15, 0],
        }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* ── Orb secondaire (bas-droite) ── */}
      <motion.div
        className="absolute -bottom-20 -right-20 h-[360px] w-[360px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(62,207,142,0.18) 0%, transparent 68%)",
        }}
        animate={{
          scale: [1, 1.15, 1],
          x: [0, -15, 0],
          y: [0, -20, 0],
        }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
      />

      {/* ── Orb accent (centre-droit) ── */}
      <motion.div
        className="absolute -right-24 top-1/2 h-[260px] w-[260px] rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(145,117,255,0.15) 0%, transparent 70%)",
        }}
        animate={{
          scale: [1, 1.2, 1],
          y: [0, -30, 0],
        }}
        transition={{ duration: 13, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />

      {/* ── Anneaux concentriques pulsants ── */}
      <div className="absolute inset-0 flex items-center justify-center">
        {[1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            className="absolute rounded-full border border-[#472cb3]/10"
            initial={{ width: i * 120, height: i * 120, opacity: 0.5 }}
            animate={{
              width: [i * 120, i * 130, i * 120],
              height: [i * 120, i * 130, i * 120],
              opacity: [0.5 / i, 0.8 / i, 0.5 / i],
            }}
            transition={{
              duration: 5 + i * 1.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.6,
            }}
          />
        ))}
      </div>

      {/* ── Wave SVG continue (bas du panel) ── */}
      <div className="absolute bottom-0 left-0 right-0 h-40 overflow-hidden">
        <motion.svg
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          className="absolute bottom-0 h-40 w-[200%]"
          animate={{ x: [0, "-50%"] }}
          transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
        >
          <path
            d="M0,60 C120,100 240,20 360,60 C480,100 600,20 720,60 C840,100 960,20 1080,60 C1200,100 1320,20 1440,60 L1440,120 L0,120 Z"
            fill="rgba(71,44,179,0.07)"
          />
          <path
            d="M0,80 C120,50 240,110 360,80 C480,50 600,110 720,80 C840,50 960,110 1080,80 C1200,50 1320,110 1440,80 L1440,120 L0,120 Z"
            fill="rgba(62,207,142,0.05)"
          />
        </motion.svg>
      </div>

      {/* ── Particules flottantes ── */}
      {[
        { cx: "20%", cy: "30%", r: 3, delay: 0 },
        { cx: "75%", cy: "15%", r: 2, delay: 1.2 },
        { cx: "85%", cy: "55%", r: 4, delay: 2.4 },
        { cx: "15%", cy: "70%", r: 2.5, delay: 0.8 },
        { cx: "55%", cy: "80%", r: 2, delay: 3.1 },
        { cx: "40%", cy: "20%", r: 1.5, delay: 1.8 },
        { cx: "65%", cy: "65%", r: 3, delay: 4.0 },
      ].map(({ cx, cy, r, delay }, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-[#472cb3]/30"
          style={{ left: cx, top: cy, width: r * 2, height: r * 2 }}
          animate={{
            y: [0, -16, 0],
            opacity: [0.4, 0.9, 0.4],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 4 + i * 0.7,
            repeat: Infinity,
            ease: "easeInOut",
            delay,
          }}
        />
      ))}

      {/* ── Trait diagonal décoratif ── */}
      <svg
        className="absolute inset-0 h-full w-full opacity-[0.06]"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <line x1="0" y1="100%" x2="100%" y2="0" stroke="#472cb3" strokeWidth="2" />
        <line x1="-10%" y1="100%" x2="90%" y2="0" stroke="#3ecf8e" strokeWidth="1.5" />
      </svg>
    </div>
  );
};
